import { Component, computed, input } from '@angular/core';

interface TKernLetter {
  char: string;
  kind: 'KEEP' | 'REMOVED' | 'ADDED';
}

/**
 * Aligns `from` onto `to` letter by letter (longest common subsequence), so only the letters that actually change
 * are marked (e.g. loop -> lop removes the second o, bal -> ball adds an l).
 */
function diffLetters(from: string, to: string): TKernLetter[] {
  const lcs = Array.from({ length: from.length + 1 }, () => new Array<number>(to.length + 1).fill(0));
  for (let i = from.length - 1; i >= 0; i--) {
    for (let j = to.length - 1; j >= 0; j--) {
      lcs[i][j] = from[i] === to[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const letters: TKernLetter[] = [];
  let i = 0;
  let j = 0;
  while (i < from.length && j < to.length) {
    if (from[i] === to[j]) {
      letters.push({ char: from[i++], kind: 'KEEP' });
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      letters.push({ char: from[i++], kind: 'REMOVED' });
    } else {
      letters.push({ char: to[j++], kind: 'ADDED' });
    }
  }
  while (i < from.length) letters.push({ char: from[i++], kind: 'REMOVED' });
  while (j < to.length) letters.push({ char: to[j++], kind: 'ADDED' });

  return letters;
}

/**
 * The kern of the word factory. When a morpheme changes the kern (loop + en -> lop-en), the change is played in place:
 * CHANGED colours the kern in the stem colour while slowly collapsing the letters that go (and expanding added ones).
 */
@Component({
  selector: 'rid-word-building-kern',
  templateUrl: './rid-word-building-kern.html',
  host: { class: 'block' },
})
export class RidWordBuildingKern {
  root = input.required<string>();
  // The kern the root changes into; only applied outside the ROOT phase.
  kern = input<string | undefined>(undefined);
  phase = input<'ROOT' | 'CHANGED'>('ROOT');

  protected letters = computed(() => diffLetters(this.root(), this.kern() ?? this.root()));
}
